import apiClient from './client';

/**
 * Sync consolidated item with instances to Algolia search index
 * @param {string} hash - The item hash to sync
 * @returns {Promise<Object>} Updated item data
 */
export const syncItem = async (hash) => {
  const response = await apiClient.post(`/api/items/${hash}/sync`);
  return response.data;
};

/**
 * Assign item to issue (creates ItemInstance)
 * @param {string} hash - The item hash
 * @param {Object} options - Assignment options
 * @param {number} [options.issueNumber] - Specific issue number (uses latest if omitted)
 * @param {string} [options.title] - Custom title override
 * @param {string} [options.blurb] - Custom blurb override
 * @param {string} [options.notes] - Instance-specific notes
 * @returns {Promise<Object>} Created ItemInstance
 */
export const assignItem = async (hash, options = {}) => {
  const response = await apiClient.post(`/api/items/${hash}/assign`, options);
  return response.data;
};

/**
 * Get all instances (assignments) for an item
 * @param {string} hash - The item hash
 * @returns {Promise<Object>} Object with instances array
 */
export const getItemInstances = async (hash) => {
  const response = await apiClient.get(`/api/items/${hash}/instances`);
  return response.data;
};

/**
 * Get instances assigned to a specific issue
 * @param {number} issueNumber - The issue number
 * @param {Object} options - Query options
 * @param {number} [options.limit=50] - Results per page
 * @param {string} [options.cursor] - Pagination cursor
 * @returns {Promise<Object>} Paginated instances result
 */
export const getInstancesByIssue = async (issueNumber, options = {}) => {
  const { limit = 50, cursor } = options;
  const params = { limit: limit.toString() };
  if (cursor) params.cursor = cursor;
  
  const response = await apiClient.get(`/api/items/instances/issue/${issueNumber}`, { params });
  return response.data;
};

/**
 * Update an item instance
 * @param {string} instanceId - The instance ID (hash:ulid)
 * @param {Object} updates - Fields to update
 * @param {string} [updates.title] - Custom title override
 * @param {string} [updates.blurb] - Custom blurb override
 * @param {string} [updates.notes] - Instance-specific notes
 * @returns {Promise<Object>} Updated instance
 */
export const updateItemInstance = async (instanceId, updates) => {
  const response = await apiClient.put(`/api/items/instances/${instanceId}`, updates);
  return response.data;
};

/**
 * Remove an item instance (unassign from issue)
 * @param {string} instanceId - The instance ID (hash:ulid)
 * @returns {Promise<void>}
 */
export const removeItemInstance = async (instanceId) => {
  await apiClient.delete(`/api/items/instances/${instanceId}`);
};

/**
 * Sync an item instance from its canonical item
 * Updates the instance with current values from the canonical item.
 * Preserves instance-specific title and category overrides.
 * @param {string} instanceId - The instance ID (hash:ulid)
 * @returns {Promise<Object>} Updated instance
 */
export const syncItemInstance = async (instanceId) => {
  const response = await apiClient.post(`/api/items/instances/${instanceId}/sync`);
  return response.data;
};

/**
 * Batch reorder item instances by updating their positions
 * @param {Array<{id: string, position: string}>} updates - Array of position updates
 * @returns {Promise<Object>} Object with updated instances and count
 */
export const reorderInstances = async (updates) => {
  const response = await apiClient.post('/api/items/instances/reorder', { updates });
  return response.data;
};

/**
 * Get item details with all instances and associated issue information
 * Used for the item detail/management page
 * @param {string} hash - The item hash
 * @returns {Promise<Object>} Object with item and instances (with issue info)
 */
export const getItemDetails = async (hash) => {
  const response = await apiClient.get(`/api/items/${hash}/details`);
  return response.data;
};

/**
 * Get a single item by hash
 * @param {string} hash - The item hash
 * @returns {Promise<Object>} The item data
 */
export const getItem = async (hash) => {
  const response = await apiClient.get(`/api/items/${hash}`);
  return response.data;
};

/**
 * Update an item
 * @param {string} hash - The item hash
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated item
 */
export const updateItem = async (hash, updates) => {
  const response = await apiClient.put(`/api/items/${hash}`, updates);
  return response.data;
};

/**
 * Reprocess an item to run AI analysis (async with task tracking)
 * Returns immediately with taskId. Results delivered via WebSocket or poll getTaskStatus().
 * @param {string} hash - The item hash
 * @returns {Promise<{message: string, hash: string, taskId: string, status: 'processing'}>}
 */
export const reprocessItem = async (hash) => {
  const response = await apiClient.post(`/api/items/${hash}/reprocess`);
  return response.data;
};

/**
 * Cancel or clear AI reprocessing state
 * Use when processing appears stuck or to cancel and restart
 * @param {string} hash - The item hash
 * @returns {Promise<{success: boolean, message: string, clearedTaskId: string|null}>}
 */
export const cancelReprocess = async (hash) => {
  const response = await apiClient.post(`/api/items/${hash}/cancel-reprocess`);
  return response.data;
};

/**
 * Get the status of a background AI task
 * @param {string} taskId - The task execution ID
 * @returns {Promise<{id: string, name: string, state: string, progressMessage?: string, progressPercent?: number, result?: Object, errorMessage?: string}>}
 */
export const getTaskStatus = async (taskId) => {
  const response = await apiClient.get(`/api/items/task/${taskId}`);
  return response.data;
};

/**
 * Delete an item and all associated instances
 * @param {string} hash - The item hash
 * @returns {Promise<void>}
 */
export const deleteItem = async (hash) => {
  await apiClient.delete(`/api/items/${hash}`);
};

/**
 * Search items in the Algolia index
 * @param {string} query - The search query
 * @param {Object} options - Search options
 * @param {number} [options.page=0] - Page number
 * @param {number} [options.limit=20] - Results per page
 * @param {string} [options.filter] - Filter type: "review" or "status:{value}"
 * @param {string} [options.sort='desc'] - Sort order: "asc" or "desc"
 * @returns {Promise<Object>} Search results with hits and metadata
 */
export const searchItems = async (query, options = {}) => {
  const { page = 0, limit = 20, filter, sort } = options;

  if (!query || query.trim() === '') {
    throw new Error('Search query is required');
  }

  const params = {
    q: query.trim(),
    page: page.toString(),
    limit: limit.toString(),
  };

  // Add optional filter and sort params
  if (filter) {
    params.filter = filter;
  }
  if (sort) {
    params.sort = sort;
  }

  const response = await apiClient.get('/api/items/search', { params });

  return response.data;
};
